import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Split 845 Contracts
 *
 * Turns one parsed X12 845 into one single-contract document per contract it
 * carries, so that each becomes its own SPA.
 *
 * ```
 * Parse EDI → Split 845 Contracts → Map 845 to SPA → Upsert SPA
 * ```
 *
 * **A contract is what a SPA corresponds to.** Each `CON` loop in an 845 nests
 * its own End Customer and its own `PAD` loops, so a document carrying 37
 * contracts states 37 agreements. The two suppliers are mirror images of one
 * structure: Rockwell states one contract across up to 81 Branches, Schneider
 * up to 37 contracts at one shared Branch.
 *
 * **This step exists to fan out, and that is why it is separate from the
 * mapper.** Its `contracts` output is an `array<json>` wired into Map 845 to
 * SPA's single-value `contract` input, so the engine runs the mapper and
 * everything downstream of it once per contract. A document carrying one
 * contract — every document in the corpus but 296 — emits a single item and is
 * not fanned out at all.
 *
 * Supplier-agnostic, and unconfigurable: a `CON` loop is a `CON` loop for every
 * sender. A party stated before the first `CON` applies to every contract; one
 * stated inside a contract applies to that contract only. Every slice keeps the
 * document's content hash, because a redelivery must still deduplicate to the
 * same SPAs — what distinguishes siblings is their contract number.
 */
export const split845ContractsStep = defineStepConfig({
  type: "split_845_contracts",
  name: "Split 845 Contracts",
  description:
    "Split a parsed X12 845 into one single-contract document per contract, each mapping to its own SPA. Wire contracts into Map 845 to SPA to run it once per contract.",
  category: "Transformation",
  libraryModule: "Pricing",

  inputSchema: z.object({
    header: z
      .record(z.string(), z.unknown())
      .describe("Transaction-level fields. Wire from Parse EDI's header."),
    lineItems: z
      .array(z.record(z.string(), z.unknown()))
      .describe("One entry per detail loop. Wire from Parse EDI's lineItems."),
  }),

  outputSchema: z.object({
    contracts: z
      .array(z.record(z.string(), z.unknown()))
      .describe(
        "One entry per contract, each a single-contract view of the document as { header, lineItems }. Wire to Map 845 to SPA's contract input, which fans this step out to one SPA per contract.",
      ),
    contractCount: z
      .number()
      .int()
      .describe(
        "contracts.length. One for all but 296 documents in the corpus; a document naming no contract at all yields one entry carrying the whole document, so nothing is dropped.",
      ),
  }),

  configSchema: z.object({}),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "2 minutes",
  },
});
